import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import PageRenderer from "./PageRenderer";
import { companyDetails } from "../data";
import { User, Phone, Mail, MapPin, CheckCircle, AlertCircle, MessageSquare, Building, FileText, ExternalLink } from "lucide-react";

export default function Kontakt() {
  const [company, setCompany] = useState(companyDetails);
  const [formData, setFormData] = useState({
    navn: "",
    telefon: "",
    email: "",
    beskrivelse: "",
    website_url: "" // Honeypot field
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error'
  const [submitMessage, setSubmitMessage] = useState("");

  useEffect(() => {
    fetch("https://api.dev.auxo.dk/public/content/virksomhedsoplysninger")
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (data && data.length > 0) {
          const item = data[0];
          setCompany({
            name: item.firmanavn?.da || item.firmanavn || companyDetails.name,
            cvr: item.cvr || companyDetails.cvr,
            address: item.adresse?.da || item.adresse || companyDetails.address,
            phone: item.telefon || companyDetails.phone,
            email: item.email || companyDetails.email,
            facebook: item.facebook_url || companyDetails.facebook,
            logo: (item.logo && item.logo[0]) || companyDetails.logo
          });
        }
      })
      .catch(() => {});
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.navn.trim()) newErrors.navn = "Navn er påkrævet";
    if (!formData.telefon.trim()) newErrors.telefon = "Telefonnummer er påkrævet";
    if (!formData.email.trim()) {
      newErrors.email = "E-mail er påkrævet";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Ugyldig e-mailadresse";
    }
    if (!formData.beskrivelse.trim()) newErrors.beskrivelse = "Beskrivelse er påkrævet";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitStatus(null);
    setSubmitMessage("");

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          navn: formData.navn,
          telefon: formData.telefon,
          email: formData.email,
          beskrivelse: formData.beskrivelse,
          website_url: formData.website_url // Honeypot
        })
      });

      if (response.ok) {
        setSubmitStatus("success");
        setSubmitMessage("Mange tak for din besked! Vi har modtaget din henvendelse og vender tilbage til dig hurtigst muligt.");
        setFormData({
          navn: "",
          telefon: "",
          email: "",
          beskrivelse: "",
          website_url: ""
        });
      } else {
        const errData = await response.json().catch(() => ({}));
        setSubmitStatus("error");
        setSubmitMessage(errData.message || "Der skete en fejl under indsendelsen. Prøv venligst igen eller kontakt os direkte.");
      }
    } catch (error) {
      console.error("Fejl ved indsendelse:", error);
      setSubmitStatus("error");
      setSubmitMessage("Netværksfejl. Kontroller din internetforbindelse og prøv igen.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {/* Render only the Hero banner of the Kontakt page */}
      <PageRenderer customSlug="kontakt" onlyHero={true} />

      {/* Main Content & Form Section */}
      <div className="bg-slate-50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left Column: Nicely Styled Contact Details */}
            <div className="lg:col-span-5 space-y-8">
              <div>
                <h2 className="text-3xl font-black text-slate-900 uppercase tracking-tight mb-4">
                  Kontakt os
                </h2>
                <p className="text-slate-600 text-base leading-relaxed">
                  Har du spørgsmål til holdstart, priser, køretimer eller noget helt fjerde? Du er altid velkommen til at kontakte os. Ring, send en SMS eller udfyld formularen, så vender vi tilbage hurtigst muligt!
                </p>
              </div>

              {/* Contact Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
                {/* Phone Card */}
                <a 
                  href={`tel:${company.phone.replace(/\s+/g, "")}`} 
                  className="flex items-start gap-4 p-6 bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-[#00D492]/50 transition-all group"
                >
                  <div className="p-3.5 bg-[#00D492]/10 text-[#00D492] rounded-xl group-hover:bg-[#00D492] group-hover:text-white transition-all shrink-0">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Telefon</span>
                      <span className="px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-[#00D492]/10 text-[#00D492] rounded-full">Ring eller SMS</span>
                    </div>
                    <p className="text-lg font-extrabold text-slate-900 mt-1 group-hover:text-[#00D492] transition-colors">
                      {company.phone}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">Vi svarer hurtigt på både opkald og SMS</p>
                  </div>
                </a>

                {/* Email Card */}
                <a 
                  href={`mailto:${company.email}`} 
                  className="flex items-start gap-4 p-6 bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-[#00D492]/50 transition-all group"
                >
                  <div className="p-3.5 bg-[#00D492]/10 text-[#00D492] rounded-xl group-hover:bg-[#00D492] group-hover:text-white transition-all shrink-0">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-400">E-mail</span>
                    <p className="text-lg font-extrabold text-slate-900 mt-1 group-hover:text-[#00D492] transition-colors break-all">
                      {company.email}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">Vi besvarer alle mails inden for 24 timer</p>
                  </div>
                </a>

                {/* Address Card */}
                <div className="flex items-start gap-4 p-6 bg-white rounded-2xl border border-slate-200 shadow-sm sm:col-span-2 lg:col-span-1">
                  <div className="p-3.5 bg-[#00D492]/10 text-[#00D492] rounded-xl shrink-0">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Adresse</span>
                    <p className="text-lg font-extrabold text-slate-900 mt-1">
                      {company.address}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">Centralt beliggende i Silkeborg</p>
                  </div>
                </div>
              </div>

              {/* Extra Details Block */}
              <div className="p-6 bg-white rounded-2xl border border-slate-200 shadow-sm space-y-4">
                <div className="flex items-center gap-3 text-slate-700">
                  <Building className="w-5 h-5 text-slate-400 shrink-0" />
                  <span className="text-sm font-medium">CVR-nummer: <strong className="text-slate-900">{company.cvr}</strong></span>
                </div>
                <div className="border-t border-slate-100 pt-4">
                  <a 
                    href="https://assets.auxodev.dk/assets/354bdcac-d302-42dd-b3d6-dafad7605e7e/01135a83-44de-4c32-81db-cc627c2ee060.pdf" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-bold text-[#00D492] hover:text-[#00bf80] transition-colors group"
                  >
                    <FileText className="w-5 h-5" />
                    <span>Læs om persondata og samtykke her</span>
                    <ExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                </div>
              </div>
            </div>

            {/* Right Column: Contact Form */}
            <div className="lg:col-span-7 bg-white p-8 md:p-10 rounded-2xl border border-slate-200 shadow-lg">
              <div className="mb-8">
                <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tight mb-2">
                  Send os en besked
                </h3>
                <p className="text-slate-600 text-sm">
                  Udfyld formularen nedenfor, så kontakter vi dig hurtigst muligt.
                </p>
              </div>

              {submitStatus === "success" && (
                <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-r-lg mb-8 flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-emerald-900 text-lg">Besked sendt!</h4>
                    <p className="text-emerald-800 mt-1">{submitMessage}</p>
                  </div>
                </div>
              )}

              {submitStatus === "error" && (
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg mb-8 flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-red-600 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-red-900 text-lg">Der opstod en fejl</h4>
                    <p className="text-red-800 mt-1">{submitMessage}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot field (absolutely hidden from humans) */}
                <div className="sr-only absolute -top-9999 -left-9999">
                  <label htmlFor="website_url">Efterlad dette felt tomt</label>
                  <input
                    id="website_url"
                    type="text"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                {/* Name Field */}
                <div>
                  <label htmlFor="navn" className="block text-sm font-bold text-slate-700 mb-1.5">
                    Navn *
                  </label>
                  <div className="relative">
                    <input
                      id="navn"
                      type="text"
                      name="navn"
                      value={formData.navn}
                      onChange={handleChange}
                      placeholder="Dit fulde navn"
                      className={`w-full pl-10 pr-4 py-3 bg-white border rounded-lg text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all ${
                        errors.navn ? "border-red-500 ring-2 ring-red-500/10" : "border-slate-300"
                      }`}
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                      <User className="w-5 h-5" />
                    </div>
                  </div>
                  {errors.navn && <p className="text-red-500 text-xs font-semibold mt-1">{errors.navn}</p>}
                </div>

                {/* Phone & Email Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Phone Field */}
                  <div>
                    <label htmlFor="telefon" className="block text-sm font-bold text-slate-700 mb-1.5">
                      Telefonnummer *
                    </label>
                    <div className="relative">
                      <input
                        id="telefon"
                        type="tel"
                        name="telefon"
                        value={formData.telefon}
                        onChange={handleChange}
                        placeholder="Dit telefonnummer"
                        className={`w-full pl-10 pr-4 py-3 bg-white border rounded-lg text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all ${
                          errors.telefon ? "border-red-500 ring-2 ring-red-500/10" : "border-slate-300"
                        }`}
                      />
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                        <Phone className="w-5 h-5" />
                      </div>
                    </div>
                    {errors.telefon && <p className="text-red-500 text-xs font-semibold mt-1">{errors.telefon}</p>}
                  </div>

                  {/* Email Field */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-bold text-slate-700 mb-1.5">
                      Email *
                    </label>
                    <div className="relative">
                      <input
                        id="email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="din@email.dk"
                        className={`w-full pl-10 pr-4 py-3 bg-white border rounded-lg text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all ${
                          errors.email ? "border-red-500 ring-2 ring-red-500/10" : "border-slate-300"
                        }`}
                      />
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                        <Mail className="w-5 h-5" />
                      </div>
                    </div>
                    {errors.email && <p className="text-red-500 text-xs font-semibold mt-1">{errors.email}</p>}
                  </div>
                </div>

                {/* Description Field */}
                <div>
                  <label htmlFor="beskrivelse" className="block text-sm font-bold text-slate-700 mb-1.5">
                    Beskrivelse *
                  </label>
                  <div className="relative">
                    <textarea
                      id="beskrivelse"
                      name="beskrivelse"
                      rows="5"
                      value={formData.beskrivelse}
                      onChange={handleChange}
                      placeholder="Skriv din besked her..."
                      className={`w-full pl-10 pr-4 py-3 bg-white border rounded-lg text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all ${
                        errors.beskrivelse ? "border-red-500 ring-2 ring-red-500/10" : "border-slate-300"
                      }`}
                    />
                    <div className="absolute top-3 left-3 flex items-center pointer-events-none text-slate-400">
                      <MessageSquare className="w-5 h-5" />
                    </div>
                  </div>
                  {errors.beskrivelse && <p className="text-red-500 text-xs font-semibold mt-1">{errors.beskrivelse}</p>}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-[#00D492] hover:bg-[#00bf80] text-white font-bold text-base rounded-lg shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none cursor-pointer"
                >
                  {isSubmitting ? "Sender besked..." : "Send besked"}
                </button>
              </form>
            </div>

          </div>
        </div>
      </div>

      {/* Google Maps Section */}
      <section className="bg-white">
        <div className="overflow-hidden rounded-none border border-slate-200 bg-slate-50 shadow-sm">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d58249.53992313496!2d9.48208639082832!3d56.16121309387021!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x464b8b389224816f%3A0x92385f81aa4386a6!2sK%C3%B8reskolen%20AG!5e1!3m2!1sen!2sdk!4v1779977240199!5m2!1sen!2sdk"
            width="600"
            height="450"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="h-[450px] w-full transition duration-300 ease-out grayscale-0 brightness-100 hover:brightness-110"
          />
        </div>
      </section>
    </div>
  );
}
